// 3rd party imports
import React, { useEffect, useState } from "react";
import { Row, Col, Space } from "antd";
import { useSelector } from "react-redux";
import store, { RootState } from "store";

// Local imports
import ListCard from "../Shared/ListCard";
import ContactSearch from "../Shared/ContactSearch";
import UserList from "./UserList";
import Message from "../../Common/Components/Message";
import FloatingSidebarTwo from "modules/Common/Components/FloatingSidebarTwo";
import PeopleAndList from "../CampaignAssets/Components/PeopleAndList";
import { getQueryCampaignId } from "modules/EmailBrowser/utils";
import {
  linkAudienceWithAsset,
  getListForAsset,
  getContactsForAsset,
  setupListForOnBoarding,
} from "../Audience/actions";
import ButtonRightIconGreen from "modules/Common/Components/ButtonRightIconGreen";
import PlusIcon from "../../../assets/img/CuratedContent/Plus.svg";

// style components
import styled from "styled-components";
import { ASSET_AUDIENCE, CAMPAIGN_AUDIENCE } from "constants/actionTypes";
import AssetsCardPlaceHolder from "../AssetsWithOutCampaigns/Components/AssetsCardPlaceHolder";
import ContactsListItemLoaderPlaceholder from "../Audience/Lists/ContactsListItemLoaderPlaceholder";
import { closeOverlay, openOverlay } from "modules/GlobalActions";
import { IDS_CAMPAIGNS_AUDIENCE } from "constants/onBoardingIds";
import ListCreationOnBoarding from "./ListCreationOnBoarding";
import { setCampaignAudienceListShow } from "modules/OnBoarding/actions";
import InfiniteLoader from "modules/Common/Components/InfiniteLoader";
import { IContactSorting } from "../Audience/interface";
import { getRecommendedPeople, getRecommendedLists } from "modules/Campaigns/CampaignAssets/actions";
interface IContactListProps {
  isAudiencePage?: boolean;
}
const ContactList: React.FC<IContactListProps> = ({ isAudiencePage }) => {
  const onBoardingContactIds = [111, 112];
  const isOnBoarding = true;
  const pageLimit = 20;

  // local component states
  const campaignId = getQueryCampaignId();
  const listCardColumnSpan = isAudiencePage ? 5 : 6;
  const [toggleAudienceAsider, setToggleAudienceAsider] = useState(false);
  const campaignAudience: any = useSelector((state: RootState) => state.campaignAudience);
  const [selectedContactIds, setSelectedContactIds] = useState(Array<number>());
  const [selectedListIds, setSelectedListIds] = useState(Array<number>());
  const [searchValue, setSearchValue] = useState("");
  const [isSearch, setIsSearch] = useState(false);
  const [sortObj, setSortObj] = useState<IContactSorting>({
    sortBy: "engagement_score",
    sortType: "DESC",
  });
  const [firstMount, setFirstMount] = useState(true);

  // from redux store references
  const showOnBoarding = useSelector((state: RootState) => state.onBoarding.showCampaignAudienceListSelection);
  const isOnBoardingEnabled = useSelector((state: RootState) => state.user.campaignAudience) === 1;
  const overlayState = useSelector((state: RootState) => state.uiReducer.overlay);

  useEffect(() => {
    if (campaignAudience.data.lists.length === 0) {
      getListForAsset(campaignId);
    }
    if (campaignAudience.data.contacts.length === 0) {
      getContactsForAsset(sortObj.sortBy, sortObj.sortType, campaignId, 0, pageLimit);
    }

    return function cleanUp() {
      store.dispatch({
        type: ASSET_AUDIENCE.RESET,
      });
    };
  }, []);

  const toggleAsider = () => {
    getRecommendedPeople();
    getRecommendedLists();
    setToggleAudienceAsider(!toggleAudienceAsider);
    store.dispatch({
      type: CAMPAIGN_AUDIENCE.SET_LINK_ASIDER_STATE,
      linkAsider: !toggleAudienceAsider,
    });
    openOverlay();
  };

  const selectPeople = (id: number, name: string, audience_type: string) => {
    if (audience_type === "contact") {
      if (selectedContactIds.indexOf(id) < 0) {
        setSelectedContactIds([...selectedContactIds, id]);
      } else {
        handleRemovePeople(id, name, audience_type);
      }
    } else if (audience_type === "list") {
      if (selectedListIds.indexOf(id) < 0) {
        setSelectedListIds([...selectedListIds, id]);
      } else {
        handleRemovePeople(id, name, audience_type);
      }
    }
  };

  function handleRemovePeople(id: number, name: string, audience_type: string) {
    if (audience_type === "contact") {
      const updatedContactIds = [...selectedContactIds];
      let index = updatedContactIds.indexOf(id);
      if (index >= 0) {
        updatedContactIds.splice(index, 1);
        setSelectedContactIds(updatedContactIds);
      }
    } else if (audience_type === "list") {
      const updatedListIds = [...selectedListIds];
      let index = updatedListIds.indexOf(id);
      if (index >= 0) {
        updatedListIds.splice(index, 1);
        setSelectedListIds(updatedListIds);
      }
    }
  }

  const linkAudience = () => {
    const isListSelected = selectedListIds.length > 0 ? true : false;
    if (selectedListIds.length > 0 || selectedContactIds.length > 0) {
      if (isListSelected && showOnBoarding && isOnBoardingEnabled) {
        setupListForOnBoarding();
      } else {
        linkAudienceWithAsset(campaignId, selectedListIds, selectedContactIds);
      }
      setSelectedListIds([]);
      setSelectedContactIds([]);
    }
    store.dispatch({
      type: CAMPAIGN_AUDIENCE.SET_LINK_ASIDER_STATE,
      linkAsider: !toggleAudienceAsider,
    });
    setToggleAudienceAsider(false);
    closeOverlay();
  };

  const cleanSearchField = () => {
    setIsSearch(false);
    setSearchValue("");
    getContactsForAsset(sortObj.sortBy, sortObj.sortType, campaignId, 0, pageLimit, "", true);
    store.dispatch({
      type: ASSET_AUDIENCE.ERROR_SEARCH_TERM,
      payload: { message: "", success: true },
    });
  };

  const searchContacts = (e: any, value?: string) => {
    e.stopPropagation();
    let searchterm = e.target.value === undefined ? value : e.target.value;
    setSearchValue(searchterm);
    if (e.which === 13 || e.which === 97 || e.currentTarget.classList.contains("search-icon")) {
      setIsSearch(true);

      getContactsForAsset(sortObj.sortBy, sortObj.sortType, campaignId, 0, pageLimit, searchterm, true);
    } else if (!searchterm) {
      setIsSearch(false);
      getContactsForAsset(sortObj.sortBy, sortObj.sortType, campaignId, 0, pageLimit, "", true);
    }
  };

  useEffect(() => {
    if (toggleAudienceAsider && !overlayState) setToggleAudienceAsider(false);
  }, [overlayState]);

  useEffect(() => {
    if (!firstMount) {
      getContactsForAsset(sortObj.sortBy, sortObj.sortType, campaignId, 0, pageLimit, searchValue, true, false);
    } else {
      setFirstMount(false);
    }
  }, [sortObj]);

  return (
    <Wrapper>
      <div className="mb-3">
        <Message
          id={IDS_CAMPAIGNS_AUDIENCE.MESSAGE}
          message="Any contact or list added here will be added to all the Emails within this campaign."
          label="Heads up!"
          type="success"
        />
      </div>

      {isAudiencePage ? (
        <Row>
          <Col span={18}>
            <h3>Contact Lists:</h3>
          </Col>
          <Col span={6}>
            <div className="right-aligned">
              <ButtonRightIconGreen
                id={IDS_CAMPAIGNS_AUDIENCE.LINK_LIST_BUTTON}
                label="Link a List"
                icon={PlusIcon}
                onClick={toggleAsider}
              />
            </div>
          </Col>
        </Row>
      ) : (
        <h3>Contact Lists:</h3>
      )}

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 40, xl: 56, xxl: 56 }}>
        <>
          {campaignAudience.lists_loading ? (
            <Col span={24}>
              <AssetsCardPlaceHolder loading={campaignAudience.lists_loading} />
            </Col>
          ) : campaignAudience.data.lists?.length > 0 ? (
            <>
              {campaignAudience.data.lists?.map((list: any, index: number) => {
                return (
                  <Col
                    key={index}
                    className="gutter-row"
                    span={listCardColumnSpan}
                    xs={18}
                    sm={12}
                    md={8}
                    lg={6}
                    xxl={4}
                    // id={IDS_CAMPAIGNS_AUDIENCE.LISTS_COLLECTION}
                  >
                    <ListCard
                      id={showOnBoarding ? IDS_CAMPAIGNS_AUDIENCE.LISTS_COLLECTION : `list-card-${index}`}
                      list={list}
                      isList={true}
                      classname={"gutter-row"}
                    />
                  </Col>
                );
              })}
            </>
          ) : (
            <Col className="gutter-row mb-4" span={24}>
              You haven’t added any List to this campaign yet.
            </Col>
          )}
        </>
      </Row>

      <Row gutter={12}>
        <Col className="gutter-row" span={isAudiencePage ? 12 : 18}>
          <h3>Individual Contacts added</h3>
        </Col>
        {isAudiencePage ? (
          <Col className="gutter-row" span={12}>
            <div className="right-aligned">
              <Space>
                <ContactSearch
                  cleanSearchField={() => cleanSearchField()}
                  searchContacts={(value: any, value1?: string) => searchContacts(value, value1)}
                  searchValue={searchValue}
                />
                <ButtonRightIconGreen
                  id={IDS_CAMPAIGNS_AUDIENCE.LINK_CONTACT_BUTTON}
                  label="Link a Contact"
                  icon={PlusIcon}
                  onClick={toggleAsider}
                ></ButtonRightIconGreen>
              </Space>
            </div>
          </Col>
        ) : (
          <Col className="gutter-row" span={6}>
            <ContactSearch
              cleanSearchField={() => cleanSearchField()}
              searchContacts={(value: any, value1?: string) => searchContacts(value, value1)}
              searchValue={searchValue}
            />
          </Col>
        )}
      </Row>

      <InfiniteLoader
        dataLength={campaignAudience.data.contacts.length}
        next={() =>
          getContactsForAsset(
            sortObj.sortBy,
            sortObj.sortType,
            campaignId,
            campaignAudience.data.contacts.length,
            pageLimit,
            searchValue
          )
        }
        hasMore={campaignAudience.data.contacts.length > 0}
        loader={<ContactsListItemLoaderPlaceholder loading={campaignAudience.contacts_loading} count={4} />}
      >
        <Row gutter={56}>
          <Col id={IDS_CAMPAIGNS_AUDIENCE.CONTACTS} className="gutter-row audience-content-inner" span={24}>
            <UserList
              contacts={campaignAudience.data.contacts}
              fromCampaign={true}
              isSearch={isSearch}
              sortObj={sortObj}
              setSortObj={setSortObj}
            />
          </Col>
        </Row>
      </InfiniteLoader>

      <ContactsListItemLoaderPlaceholder
        loading={campaignAudience.contacts_loading && campaignAudience.data.contacts.length === 0}
        count={4}
      />

      <FloatingSidebarTwo
        className="audience-sidebar"
        isToggle={toggleAudienceAsider}
        dataId="link-contact-audience-asider"
      >
        <PeopleAndList
          inContactList={true}
          // isResponsive={false}
          onClickAudienceDone={linkAudience}
          handleAddPeople={(id: number, name: string, audienceType: string) => {
            selectPeople(id, name, audienceType);
          }}
          addedContactIds={
            showOnBoarding ? onBoardingContactIds : campaignAudience.data.contacts.map((item: any) => item?.id)
          }
          addedListIds={campaignAudience.data.lists.map((item: any) => item?.id)}
          selectedContactIds={selectedContactIds}
          selectedListIds={selectedListIds}
        />
      </FloatingSidebarTwo>
      {/* {showOnBoarding && <ListCreationOnBoarding handleNext={() => setCampaignAudienceListShow(false)} />} */}
    </Wrapper>
  );
};

export default ContactList;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding-top: 5px;
  h3 {
    font-size: 22px;
    color: #67696a;
    font-weight: 500;
    font-family: "robotomedium";
    margin-bottom: 15px;
    margin-top: 5px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 16px;
    }
  }
  .right-aligned {
    display: flex;
    justify-content: flex-end;
  }
  .bottom-margined {
    margin-bottom: 55px;
  }
  .audience-content-inner {
    position: relative;
    z-index: 5;
    .floating-footer {
      z-index: 6;
    }
    > div {
      z-index: 5;
    }
  }
`;
